import React from 'react';
import { AnyEventObject, Machine, assign } from 'xstate';

import { AccountField } from './forms';

const WELCOME_TRADE_CUSTOMER_THRESHOLD_MS = 20000;

export const welcomeTradeCustomer = (uid?: number, threshold?: number) => {
  if (!uid) return false;
  const now = Date.now();
  const thresholdBefore = now - (threshold || WELCOME_TRADE_CUSTOMER_THRESHOLD_MS);

  return uid > thresholdBefore && uid < now;
};

export enum AuthStep {
  REQUEST_CODE = 'requestCode',
  VERIFY_CODE = 'verifyCode',
}

export enum AuthStepAction {
  PREVIOUS = 'PREVIOUS',
  SUBMIT = 'SUBMIT',
}

// The form fields
export interface AuthContextFields {
  [AccountField.MOBILE]: string;
  [AccountField.VERIFICATION_CODE]: string;
}

// The machine context
export interface AuthContext {
  data: AuthContextFields;
}

// The default machine context values
export const initialAuthContext: AuthContext = {
  data: {
    [AccountField.MOBILE]: '',
    [AccountField.VERIFICATION_CODE]: '',
  },
};

const submitActionHandler = (target?: AuthStep) => ({
  target,
  actions: ['update', 'submit'],
});

const authMachineStates = {
  [AuthStep.REQUEST_CODE]: {
    on: {
      [AuthStepAction.SUBMIT]: submitActionHandler(AuthStep.VERIFY_CODE),
    },
  },
  [AuthStep.VERIFY_CODE]: {
    on: {
      [AuthStepAction.PREVIOUS]: AuthStep.REQUEST_CODE,
      [AuthStepAction.SUBMIT]: submitActionHandler(),
    },
  },
};

const authMachineActions = {
  actions: {
    update: assign({
      data: (context: AuthContext, event: AnyEventObject) => ({
        ...context.data,
        ...event.data,
      }),
    }),
    submit: (_context: AuthContext, event: AnyEventObject) => {
      // dispatch to redux
      if (event.callback) {
        event.callback();
      }
    },
  },
};

export const authMachine = Machine<AuthContext>(
  {
    id: 'authMachine',
    initial: AuthStep.REQUEST_CODE,
    context: initialAuthContext,
    states: authMachineStates,
  },
  authMachineActions
);

export const AuthStateContext = React.createContext<any>(initialAuthContext);
